import React from "react";
import siteConfig from "../config/siteConfig";
import {
  PolicyLayout,
  PolicyNotice,
  PolicySection,
} from "./Terms";
import "../css/privacy.scss";

const privacySections = [
  { id: "privacy-overview", label: "Privacy at a glance" },
  { id: "information", label: "Information in the interface" },
  { id: "browser-storage", label: "Browser storage" },
  { id: "uses", label: "How information is used" },
  { id: "sharing", label: "Sharing and third parties" },
  { id: "retention-security", label: "Retention and security" },
  { id: "choices", label: "Your choices" },
  { id: "children-updates", label: "Children and updates" },
];

function Privacy() {
  return (
    <PolicyLayout
      eyebrow="Legal · Privacy"
      intro={`A plain-language explanation of what ${siteConfig.brandName} collects, how checkout information is used, and the controls available to you.`}
      modifier="privacy"
      sections={privacySections}
      title="Privacy policy"
    >
      <PolicyNotice tone="success" title="Clear data use">
        <p>
          Checkout details are used to prepare your order and redirect you to our
          secure payment endpoint. We do not request card details on this page.
        </p>
      </PolicyNotice>

      <PolicySection id="privacy-overview" title="1. Privacy at a glance">
        <p>
          We designed this storefront so its core browsing experience requires as
          little personal information as possible. Product pages can be viewed
          without an account. Cart and wishlist selections are stored locally in
          your browser so they remain available when you return on the same device.
        </p>
        <div className="policy-page__cards">
          <article>
            <h3>Stored on this device</h3>
            <p>Product IDs, selected options, and quantities in your cart or wishlist.</p>
          </article>
          <article>
            <h3>Payment details</h3>
            <p>Card details are handled by the payment page and are not requested in our checkout form.</p>
          </article>
          <article>
            <h3>No account required</h3>
            <p>You can browse, filter, search, and build a cart without creating a profile.</p>
          </article>
          <article>
            <h3>You stay in control</h3>
            <p>Clear the cart or wishlist in the interface, or remove site data in your browser.</p>
          </article>
        </div>
      </PolicySection>

      <PolicySection id="information" title="2. Information in the interface">
        <p>
          The site presents fields used to provide support and complete orders.
        </p>
        <div className="policy-page__table-wrap">
          <table className="policy-page__table">
            <thead>
              <tr>
                <th scope="col">Area</th>
                <th scope="col">Information you may enter</th>
                <th scope="col">What this build does</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Contact form</td>
                <td>Name, email, subject, order reference, and message</td>
                <td>Validates locally, shows a confirmation state, then resets the form</td>
              </tr>
              <tr>
                <td>Checkout</td>
                <td>Contact email and delivery address</td>
                <td>Creates the payment request and supports order delivery</td>
              </tr>
              <tr>
                <td>Search and filters</td>
                <td>Search terms, categories, price ranges, and sorting choices</td>
                <td>Filters the local product catalog in the page</td>
              </tr>
              <tr>
                <td>Cart and wishlist</td>
                <td>Product IDs, options, and quantities</td>
                <td>Saves the selection in browser local storage</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p>
          Do not include passwords, health information, card numbers, or other
          sensitive data in free-text fields.
        </p>
      </PolicySection>

      <PolicySection id="browser-storage" title="3. Browser storage">
        <p>
          Local storage is a feature of your browser that keeps small pieces of data
          on your device. We use it for cart and wishlist continuity. These records
          contain catalog identifiers and selections—not your name, email, delivery
          address, or payment details.
        </p>
        <dl className="policy-page__facts">
          <div>
            <dt>Cart storage</dt>
            <dd>Product ID, quantity, and selected product options.</dd>
          </div>
          <div>
            <dt>Wishlist storage</dt>
            <dd>Product IDs you have chosen to save.</dd>
          </div>
          <div>
            <dt>Duration</dt>
            <dd>Until you clear the list or remove this site&apos;s data in browser settings.</dd>
          </div>
          <div>
            <dt>Transfer</dt>
            <dd>Local-storage values are not transmitted by this front-end build.</dd>
          </div>
        </dl>
        <p>
          Blocking local storage does not prevent basic browsing, but the cart and
          wishlist may not survive a refresh or future visit.
        </p>
      </PolicySection>

      <PolicySection id="uses" title="4. How information is used">
        <p>The interface uses the values you provide to:</p>
        <ul>
          <li>display and update your cart, wishlist, estimated total, and selected options;</li>
          <li>validate required fields and show useful error or success messages;</li>
          <li>filter and sort the local product catalog based on your choices;</li>
          <li>support the sequence of delivery, payment, and confirmation screens; and</li>
          <li>support accessibility features such as field labels and live status messages.</li>
        </ul>
        <p>
          This build does not use form values for advertising, profiling, automated
          decision-making, or marketing emails.
        </p>
      </PolicySection>

      <PolicySection id="sharing" title="5. Sharing and third parties">
        <p>
          We do not sell personal information. Checkout details are used only to
          create the payment request and support order processing.
        </p>
        <p>
          The site may link to an external website or service. Following that link
          takes you outside this storefront, and the external provider&apos;s own terms
          and privacy notice apply. Service providers may process limited data for
          hosting, payments, fraud prevention, customer care, or delivery.
        </p>
      </PolicySection>

      <PolicySection id="retention-security" title="6. Retention and security">
        <p>
          Form state normally lasts only while the relevant page is open and is
          cleared after a successful submission or checkout reset. Cart
          and wishlist records remain in local storage as described above.
        </p>
        <p>
          We use reasonable front-end safeguards, including input validation and no
          intentional persistence of payment-shaped fields. No website or device is
          perfectly secure. Protect access to your device and avoid entering
          sensitive information into a public or shared browser.
        </p>
      </PolicySection>

      <PolicySection id="choices" title="7. Your choices">
        <p>You can control locally stored data without contacting us:</p>
        <ol>
          <li>Remove individual items or clear your cart and wishlist in the storefront.</li>
          <li>Use your browser&apos;s site-data settings to delete local storage for this domain.</li>
          <li>Use private-browsing mode if you do not want selections retained after the session.</li>
          <li>Do not submit a form if you do not want its value held temporarily in page memory.</li>
        </ol>
        <p>
          For privacy questions, email <a href={`mailto:${siteConfig.contact.email}`}>{siteConfig.contact.email}</a>.
          You may also ask about access, correction, or deletion of information
          associated with an order.
        </p>
      </PolicySection>

      <PolicySection id="children-updates" title="8. Children and updates">
        <p>
          This storefront is intended for a general audience and is not designed to
          collect information from children. If you are not old enough to make a
          purchase where you live, use the shopping experience only with a parent
          or guardian.
        </p>
        <p>
          We will revise this policy if data practices change, particularly before
          adding accounts, analytics, messaging, or other order services.
          The revision date at the top helps you identify the current version.
        </p>
      </PolicySection>
    </PolicyLayout>
  );
}

export default Privacy;
